import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Ruler, Plus, Edit2, Trash2, Eye, EyeOff, Save, X } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const SizeCategoryManager = () => {
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState({
    sizing_enabled: 'true',
    sizing_field_label: 'Size',
    allow_size_override: 'false'
  });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    category_name: '',
    category_label: '',
    description: '',
    display_order: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const [categoriesRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/size-categories?includeInactive=true`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/size-categories/settings`)
      ]);
      
      setCategories(categoriesRes.data);
      setSettings(settingsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Failed to load size categories');
      setLoading(false);
    }
  };

  const handleSettingsChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveSettings = async () => {
    console.log('[SizeCategoryManager] handleSaveSettings called with settings:', settings);
    try {
      const token = sessionStorage.getItem('token');
      console.log('[SizeCategoryManager] Sending PUT request to save settings...');
      await axios.put(`${API_URL}/size-categories/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('[SizeCategoryManager] Settings saved successfully');
      toast.success('Sizing settings updated successfully');
      
      // Notify other components that sizing settings have been updated
      console.log('[SizeCategoryManager] Dispatching sizeCategoryUpdated event from settings save');
      window.dispatchEvent(new Event('sizeCategoryUpdated'));
    } catch (error) {
      console.error('Save settings error:', error);
      toast.error('Failed to update settings');
    }
  };

  const handleOpenModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        category_name: category.category_name,
        category_label: category.category_label,
        description: category.description || '',
        display_order: category.display_order
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({
        category_name: '',
        category_label: '',
        description: '',
        display_order: categories.length
      });
    }
    setShowModal(true);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const token = sessionStorage.getItem('token');
      
      if (editingCategory) {
        await axios.put(
          `${API_URL}/size-categories/${editingCategory.id}`,
          categoryForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Size category updated successfully');
      } else {
        await axios.post(
          `${API_URL}/size-categories`,
          categoryForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Size category created successfully');
      }
      
      setShowModal(false);
      loadData();
      
      // Notify other components that size categories have been updated
      window.dispatchEvent(new Event('sizeCategoryUpdated'));
    } catch (error) {
      console.error('Save category error:', error);
      toast.error(error.response?.data?.error || 'Failed to save size category');
    }
  };

  const handleToggleCategory = async (id) => {
    try {
      const token = sessionStorage.getItem('token');
      await axios.patch(
        `${API_URL}/size-categories/${id}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Size category status updated');
      loadData();
      
      // Notify other components
      window.dispatchEvent(new Event('sizeCategoryUpdated'));
    } catch (error) {
      console.error('Toggle error:', error);
      toast.error('Failed to update category status');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this size category? This action cannot be undone.')) {
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      await axios.delete(`${API_URL}/size-categories/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Size category deleted successfully');
      loadData();
      
      // Notify other components
      window.dispatchEvent(new Event('sizeCategoryUpdated'));
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.error || 'Failed to delete size category');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-6">
        <Ruler className="text-primary-600" size={24} />
        <h2 className="text-2xl font-bold">Size Categories</h2>
      </div>

      <p className="text-gray-600 mb-6">
        Configure size categories for your services (e.g., dog sizes, yard sizes, vehicle types). 
        You can disable sizing entirely if your business doesn't need size-based pricing.
      </p>

      {/* Sizing Settings */}
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <h3 className="font-semibold mb-4">Sizing Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.sizing_enabled === 'true'}
                onChange={(e) => handleSettingsChange('sizing_enabled', e.target.checked ? 'true' : 'false')}
                className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
              />
              <span className="text-sm font-medium">Enable Size-Based Pricing</span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              Turn off if your business doesn't need different pricing for different sizes
            </p>
          </div>

          <div>
            <label className="label">Size Field Label</label>
            <input
              type="text"
              value={settings.sizing_field_label}
              onChange={(e) => handleSettingsChange('sizing_field_label', e.target.value)}
              placeholder="e.g., Size, Yard Size, Vehicle Type"
              className="input"
              disabled={settings.sizing_enabled !== 'true'}
            />
            <p className="text-xs text-gray-500 mt-1">
              How the size field will be labeled in your forms
            </p>
          </div>

          <div className="md:col-span-2">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.allow_size_override === 'true'}
                onChange={(e) => handleSettingsChange('allow_size_override', e.target.checked ? 'true' : 'false')}
                className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                disabled={settings.sizing_enabled !== 'true'}
              />
              <span className="text-sm font-medium">Allow Size Override in Booking</span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              When enabled, staff can override the pet's size during booking (useful if size has changed). When disabled, the pet's registered size is always used.
            </p>
          </div>
        </div>

        <button
          onClick={handleSaveSettings}
          className="mt-4 btn btn-primary flex items-center space-x-2"
        >
          <Save size={18} />
          <span>Save Settings</span>
        </button>
      </div>

      {/* Size Categories List */}
      {settings.sizing_enabled === 'true' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold">Size Categories</h3>
            <button
              onClick={() => handleOpenModal()}
              className="btn btn-primary flex items-center space-x-2"
            >
              <Plus size={18} />
              <span>Add Category</span>
            </button>
          </div>

          {categories.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-600">No size categories yet. Add your first category to get started.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {categories.map((category) => (
                <div
                  key={category.id}
                  className={`p-4 border-2 rounded-lg ${!category.is_active ? 'opacity-60 bg-gray-50' : 'border-gray-200'}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-bold text-lg">{category.category_label}</h4>
                      {category.description && (
                        <p className="text-sm text-gray-600 mt-2">{category.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2">
                        <p className="text-xs text-gray-500">Display Order: {category.display_order}</p>
                        <p className="text-xs text-gray-400">
                          ID: <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">{category.category_name}</code>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleToggleCategory(category.id)}
                        className={`p-2 rounded-lg ${
                          category.is_active
                            ? 'bg-green-100 text-green-600'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                        title={category.is_active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
                      >
                        {category.is_active ? <Eye size={18} /> : <EyeOff size={18} />}
                      </button>
                      <button
                        onClick={() => handleOpenModal(category)}
                        className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(category.id)}
                        className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Category Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">
                {editingCategory ? 'Edit Size Category' : 'Add Size Category'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleCategorySubmit} className="space-y-4">
              <div>
                <label className="label">Category Name *</label>
                <input
                  type="text"
                  value={categoryForm.category_label}
                  onChange={(e) => {
                    const label = e.target.value;
                    // Auto-generate internal name from label (lowercase, no spaces, alphanumeric only)
                    const internalName = label.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
                    setCategoryForm({ 
                      ...categoryForm, 
                      category_label: label,
                      category_name: internalName || label.toLowerCase()
                    });
                  }}
                  required
                  placeholder="e.g., Small (0-25 lbs), Petite Dogs, Tiny"
                  className="input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {editingCategory 
                    ? '✨ Renaming will automatically update all services, speeds, and bookings' 
                    : 'This is what users will see throughout the system'}
                </p>
              </div>

              {editingCategory && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    <strong>Internal ID:</strong> <code className="bg-blue-100 px-2 py-1 rounded">{categoryForm.category_name}</code>
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    The system will automatically migrate all related data when you save.
                  </p>
                </div>
              )}

              <div>
                <label className="label">Description</label>
                <textarea
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  placeholder="Optional description or details"
                  rows={2}
                  className="input"
                />
              </div>

              <div>
                <label className="label">Display Order</label>
                <input
                  type="number"
                  value={categoryForm.display_order}
                  onChange={(e) => setCategoryForm({ ...categoryForm, display_order: parseInt(e.target.value) })}
                  min="0"
                  className="input"
                />
                <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="flex-1 btn btn-primary">
                  {editingCategory ? 'Update Category' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SizeCategoryManager;
